import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Clapperboard,
  LifeBuoy,
  Boxes,
  ArrowLeft,
  Users,
  FolderOpen,
  LayoutTemplate,
  Upload,
  UserCircle,
  Mic,
  Wrench,
  Wallet,
  Flag,
  Activity,
  MessageCircle,
  FlaskConical,
  TestTube2,
  Star,
  AlertTriangle,
  MonitorPlay,
  ListChecks,
  BarChart3,
  Server,
  Globe,
  Layers,
  Target,
  Clock,
  Film,
  LineChart,
  Plus,
  PanelLeftClose,
  PanelLeftOpen,
  Loader2,
  Workflow,
  Newspaper,
} from 'lucide-react'
import { useAuth } from '@/contexts/auth-context'
import { isElectron } from '@/lib/electron'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useAppModals } from '@/contexts/app-modals-context'

interface SidebarProps {
  collapsed?: boolean
  showBackButton?: boolean
  isAdminMode?: boolean
  isMobileDrawer?: boolean
  onNavigate?: () => void
  communityUnread?: number
  onCommunityOpen?: () => void
}

export function Sidebar({ collapsed = false, showBackButton = false, isAdminMode = false, isMobileDrawer = false, onNavigate, communityUnread = 0, onCommunityOpen }: SidebarProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, isLoading: isAuthLoading } = useAuth()
  const [isCollapsed, setIsCollapsed] = useState(collapsed)

  const homeRoute = isAdminMode ? '/admin/conversion' : '/'

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault()
    if (location.pathname === homeRoute) {
      window.location.reload()
    } else {
      navigate(homeRoute)
    }
  }

  // Sync internal state with prop
  useEffect(() => {
    setIsCollapsed(collapsed)
  }, [collapsed])

  const isAdmin = false // TODO: user?.isAdmin === true (hidden for now)

  // One nav list shared by anon and authed users — the homepage spec is that
  // the sidebar looks identical regardless of auth state. Anon visitors who
  // tap an authed-only route either see an empty list (My library, Assets)
  // or get their own anon-aware view (Settings).
  // Settings + Support live in the bottom-left footer, not here.
  const navItems = [
    { icon: Plus, label: 'New video', href: '/' },
    { icon: Clapperboard, label: 'Library', href: '/library' },
    { icon: FolderOpen, label: 'Assets', href: '/assets' },
    { icon: Boxes, label: 'Skills', href: '/skills' },
    ...(isAdmin ? [
      { icon: LayoutTemplate, label: 'Templates', href: '/templates' },
    ] : []),
  ]

  const assetItems = isAdmin ? [
    { icon: Upload, label: 'Uploads', href: '/uploads' },
    { icon: UserCircle, label: 'Avatars', href: '/avatars' },
    { icon: Mic, label: 'Voices', href: '/voices' },
  ] : []

  const modSections = [
    {
      label: 'Footage App',
      items: [
        { icon: Target, label: 'Conversion', href: '/admin/conversion' },
        { icon: LineChart, label: 'Analytics', href: '/admin/analytics' },
        { icon: FolderOpen, label: 'Videos', href: '/admin/videos' },
        { icon: Users, label: 'Users', href: '/admin/users' },
        { icon: Star, label: 'Feedback', href: '/admin/feedback' },
        { icon: ListChecks, label: 'Feedback Queue', href: '/admin/mod-feedback' },
        { icon: Clock, label: 'Cronjobs', href: '/admin/cronjobs' },
        { icon: Film, label: 'Fal Requests', href: '/admin/fal-requests' },
        { icon: AlertTriangle, label: 'Issues', href: '/admin/issues' },
        { icon: Newspaper, label: 'Feed', href: '/admin/feed' },
        { icon: Workflow, label: 'Pipeline', href: '/admin/pipeline' },
        { icon: BarChart3, label: 'Meta Analysis', href: '/admin/meta-analysis-runs' },
        { icon: Layers, label: 'Clusters', href: '/admin/clusters' },
      ],
    },
    {
      label: 'Dev Tools',
      items: [
        { icon: Flag, label: 'Flags', href: '/admin/flags' },
        { icon: FlaskConical, label: 'Agent Playground', href: '/admin/agent' },
        { icon: TestTube2, label: 'Evals', href: '/admin/evals' },
        { icon: Activity, label: 'Observability', href: '/admin/observability' },
        { icon: MonitorPlay, label: 'Sessions', href: '/admin/sessions' },
        { icon: Server, label: 'Renders', href: '/admin/render-gpus' },
      ],
    },
    {
      label: 'Administration',
      items: [
        { icon: Wallet, label: 'Business Metrics', href: '/admin/profit-loss' },
        { icon: Globe, label: 'Directories', href: '/admin/directories' },
        { icon: LayoutTemplate, label: 'Submission template', href: '/admin/directory-template' },
        { icon: Wrench, label: 'App Management', href: '/admin/devtools' },
      ],
    },
  ]

  // In mobile drawer mode: always expanded, no h-screen, always visible
  const effectiveCollapsed = isMobileDrawer ? false : isCollapsed

  return (
    <aside
      className={cn(
        'flex-col transition-all duration-200',
        isMobileDrawer ? 'flex w-64 h-full' : 'h-full hidden md:flex',
        !isMobileDrawer && (effectiveCollapsed ? 'w-[72px]' : 'w-[232px]'),
        isAdminMode
          ? 'bg-slate-800 border-r border-slate-700'
          : 'bg-background border-r border-border'
      )}
    >
      {/* Logo + collapse toggle. The logo stays at a fixed left position in
          both modes — only the right-side collapse button shows when expanded.
          When collapsed, hovering the logo swaps it for an expand icon and
          clicking it expands the sidebar. */}
      <div className={cn(
        "h-[72px] flex items-center justify-between electron-drag pl-[18px] pr-3",
        isAdminMode && "border-b border-slate-700",
        isElectron() && !isMobileDrawer && "pt-[var(--electron-titlebar-height,0px)]  h-auto min-h-[72px]"
      )}>
        {effectiveCollapsed && !isMobileDrawer ? (
          <SidebarTip label="Expand sidebar">
            <button
              type="button"
              onClick={() => setIsCollapsed(false)}
              aria-label="Expand sidebar"
              className="electron-no-drag relative group inline-flex items-center justify-center"
            >
              <img
                src={isAdminMode ? "/logo-sq-white.png" : "/logo-sq-black.png"}
                alt="Footage"
                className="size-7 transition-opacity group-hover:opacity-0"
              />
              <PanelLeftOpen
                className={cn(
                  'absolute h-[18px] w-[18px] transition-opacity opacity-0 group-hover:opacity-100',
                  isAdminMode ? 'text-slate-300' : 'text-muted-foreground',
                )}
              />
            </button>
          </SidebarTip>
        ) : (
          <Link
            to={homeRoute}
            onClick={(e) => { handleLogoClick(e); onNavigate?.() }}
            className="inline-flex items-center gap-2 electron-no-drag"
          >
            <img src={isAdminMode ? "/logo-sq-white.png" : "/logo-sq-black.png"} alt="Footage" className="size-7" />
          </Link>
        )}
        {!effectiveCollapsed && !isMobileDrawer && (
          <button
            type="button"
            onClick={() => setIsCollapsed(true)}
            className={cn(
              'electron-no-drag p-1.5 rounded-md transition-colors',
              isAdminMode
                ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            )}
            aria-label="Collapse sidebar"
          >
            <PanelLeftClose className="h-[18px] w-[18px]" />
          </button>
        )}
      </div>

      {/* Back button (when on create page) */}
      {showBackButton && (
        <div className={cn("px-4", effectiveCollapsed ? "mt-3 mb-3" : "mb-2 mt-8")}>
          <SidebarTip label="Back">
            <Button
              variant="ghost"
              size="icon"
              asChild
              className="w-8 h-8 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted"
            >
              <Link to={isAdminMode ? '/admin/videos' : '/videos'}>
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
          </SidebarTip>
        </div>
      )}

      {/* Navigation */}
      <nav className={cn("flex-1 overflow-y-auto", isAdminMode ? "px-3 py-4 mt-2" : "px-3 pt-0")}>
        {isAdminMode ? (
          // Admin navigation with sections
          <div className="space-y-4">
            {modSections.map((section, si) => (
              <div key={si}>
                {section.label && !effectiveCollapsed && (
                  <div className="mb-1.5 px-3">
                    <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                      {section.label}
                    </span>
                  </div>
                )}
                <div className="space-y-0.5">
                  {section.items.map((item) => {
                    const isActive = location.pathname === item.href
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => onNavigate?.()}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors',
                          isActive
                            ? 'bg-slate-700 text-white'
                            : 'text-slate-300 hover:bg-slate-700/60 hover:text-white',
                          effectiveCollapsed && 'justify-center px-0'
                        )}
                      >
                        <item.icon className="h-5 w-5 flex-shrink-0" />
                        {!effectiveCollapsed && <span className="text-sm font-medium">{item.label}</span>}
                      </Link>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // One shared nav list — identical for anon and authed users.
          <>
            <div className="space-y-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.href
                const link = (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => onNavigate?.()}
                    className={cn(
                      // Same padding regardless of collapsed state so the icon
                      // stays at a fixed X position; only the label is hidden
                      // when collapsed.
                      'flex items-center gap-3 rounded-2xl transition-colors py-2.5 px-3.5',
                      isActive
                        ? 'bg-muted text-foreground font-medium'
                        : 'text-foreground hover:bg-muted/60',
                    )}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0 stroke-[1.75]" />
                    {!effectiveCollapsed && <span className="text-[14px] leading-none whitespace-nowrap">{item.label}</span>}
                  </Link>
                )
                return effectiveCollapsed
                  ? <SidebarTip key={item.href} label={item.label}>{link}</SidebarTip>
                  : link
              })}
            </div>

            {assetItems.length > 0 && (
              <>
                {effectiveCollapsed
                  ? <div className="border-t border-border my-1.5" />
                  : (
                    <div className="mt-6 mb-2">
                      <span className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Assets
                      </span>
                    </div>
                  )
                }
                <div className="space-y-0.5">
                  {assetItems.map((item) => {
                    const isActive = location.pathname === item.href
                    const link = (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => onNavigate?.()}
                        className={cn(
                          'flex items-center gap-3 px-3 py-1.5 rounded-lg transition-colors',
                          isActive
                            ? 'bg-muted text-foreground font-medium'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/60',
                          effectiveCollapsed && 'justify-center px-0'
                        )}
                      >
                        <item.icon className="h-[18px] w-[18px] flex-shrink-0" />
                        {!effectiveCollapsed && <span className="text-sm">{item.label}</span>}
                      </Link>
                    )
                    return effectiveCollapsed
                      ? <SidebarTip key={item.href} label={item.label}>{link}</SidebarTip>
                      : link
                  })}
                </div>
              </>
            )}

            {!isAdminMode && onCommunityOpen && (() => {
              const btn = (
                <button
                  onClick={onCommunityOpen}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-1.5 rounded-lg transition-colors text-muted-foreground hover:text-foreground hover:bg-muted/60 mt-2",
                    effectiveCollapsed && "justify-center px-0"
                  )}
                >
                  <div className="relative flex-shrink-0">
                    <MessageCircle className="h-[18px] w-[18px]" />
                    {communityUnread > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center px-1">
                        {communityUnread > 99 ? '99+' : communityUnread}
                      </span>
                    )}
                  </div>
                  {!effectiveCollapsed && (
                    <span className="text-sm">Community</span>
                  )}
                </button>
              )
              return effectiveCollapsed
                ? <SidebarTip label="Community">{btn}</SidebarTip>
                : btn
            })()}
          </>
        )}
      </nav>

      {/* Footer: Support button + Sign in / Account, stacked. Hidden in admin mode. */}
      {!isAdminMode && (
        <SidebarFooter
          user={user}
          isAuthLoading={isAuthLoading}
          effectiveCollapsed={effectiveCollapsed}
          onNavigate={onNavigate}
          active={location.pathname}
        />
      )}

      {/* User/Admin switcher pinned to the very bottom (admins only) */}
      {user?.isAdmin && !effectiveCollapsed && (
        <div className="px-3 pt-2 pb-3">
          <div className={cn('flex rounded-lg p-0.5', isAdminMode ? 'bg-slate-700' : 'bg-muted')}>
            <button
              onClick={() => { navigate('/library'); onNavigate?.() }}
              className={cn(
                'flex-1 text-xs font-medium py-1.5 rounded-md transition-colors',
                isAdminMode
                  ? 'text-slate-400 hover:text-slate-200'
                  : 'bg-background text-foreground shadow-sm',
              )}
            >
              User
            </button>
            <button
              onClick={() => { navigate('/admin'); onNavigate?.() }}
              className={cn(
                'flex-1 text-xs font-medium py-1.5 rounded-md transition-colors',
                isAdminMode
                  ? 'bg-slate-900 text-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              Admin
            </button>
          </div>
        </div>
      )}
    </aside>
  )
}

function SidebarFooter({
  user,
  isAuthLoading,
  effectiveCollapsed,
  onNavigate,
  active,
}: {
  user: { email?: string; name?: string; avatarUrl?: string; isAnonymous?: boolean } | null
  isAuthLoading: boolean
  effectiveCollapsed: boolean
  onNavigate?: () => void
  active: string
}) {
  const isAnon = !user || !!user.isAnonymous
  const { openSettings, openAuth, openSupport } = useAppModals()

  const handleSettingsClick = () => {
    if (isAuthLoading) return
    if (isAnon) openAuth('login')
    else openSettings()
    onNavigate?.()
  }

  const handleSupportClick = () => {
    openSupport()
    onNavigate?.()
  }

  const supportButton = (
    <button
      type="button"
      onClick={handleSupportClick}
      aria-label="Support"
      className={cn(
        'inline-flex items-center gap-3 rounded-2xl transition-colors py-2.5 px-3.5 text-left text-foreground hover:bg-muted/60',
        effectiveCollapsed ? 'justify-center' : 'w-full',
      )}
    >
      <LifeBuoy className="h-5 w-5 flex-shrink-0 stroke-[1.75]" />
      {!effectiveCollapsed && (
        <span className="text-[14px] leading-none whitespace-nowrap">Support</span>
      )}
    </button>
  )

  const accountLabel = isAnon ? 'Sign in' : 'Account'
  const accountButton = (
    <button
      type="button"
      onClick={handleSettingsClick}
      aria-label={accountLabel}
      className={cn(
        'inline-flex items-center gap-3 rounded-2xl transition-colors py-2.5 px-3.5 text-left',
        effectiveCollapsed ? 'justify-center' : 'w-full',
        active === '/settings'
          ? 'bg-muted text-foreground font-medium'
          : 'text-foreground hover:bg-muted/60',
      )}
    >
      <UserCircle className="h-5 w-5 flex-shrink-0 stroke-[1.75]" />
      {!effectiveCollapsed && (
        isAuthLoading ? (
          <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-muted-foreground" />
        ) : (
          <span className="text-[14px] leading-none truncate whitespace-nowrap">
            {accountLabel}
          </span>
        )
      )}
    </button>
  )

  return (
    <div className="border-t border-border flex flex-col gap-0.5 px-3 py-2.5">
      {effectiveCollapsed ? <SidebarTip label="Support">{supportButton}</SidebarTip> : supportButton}
      {effectiveCollapsed ? <SidebarTip label={accountLabel}>{accountButton}</SidebarTip> : accountButton}
    </div>
  )
}

function SidebarTip({ label, kbd, side = 'right', children }: { label: string; kbd?: string; side?: 'top' | 'right' | 'bottom' | 'left'; children: React.ReactNode }) {
  return (
    <TooltipProvider delayDuration={0} skipDelayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent
          side={side}
          sideOffset={6}
          style={{ animationDuration: '0ms' }}
          className="inline-flex items-center gap-1.5 h-6 px-2 py-0 rounded-md bg-zinc-900 text-white text-[12px] font-medium whitespace-nowrap shadow-md !animate-none"
        >
          {label}
          {kbd && (
            <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded bg-zinc-700 text-zinc-200 text-[10px]">
              {kbd}
            </span>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
